import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { StatsigProvider } from 'statsig-react';
import Box from '@mui/material/Box';

import segmentAnalytics from 'react-segment';
import { Footer } from '@universal-tennis/ui-shared';
import { StatsigContextProvider } from './hooks/StatsigContextProvider';
import { getStatsigEnvironmentTier } from './utils/statsig';

import { getAuthUser } from './redux/services/authServices';
import { getActiveTeams, getUserPreferences } from './redux/services/userService';

import Header from './components/Header';
import Routes from './routes';
import Menu from './components/Menu';
import Loading from './components/Loading';
import GenericPopup from './components/Popups/GenericPopup';
import ErrorPopup from './components/Popups/ErrorPopup';

import config from './config';

import { FooterExploreItems, FooterMoreItems, FooterProviderItems, FooterSupportItems, FooterSocialMedia } from './utils/constants';

const App = () => {
    const { user, loading: isLoadingUser, isUserLoaded } = useSelector((state) => state.auth);
    const { isMemberTeamsLoaded, isLoadingMemberTeams, isLoadingPreferences, isPreferencesLoaded } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const location = useLocation();
    segmentAnalytics.default.load(config.segmentKey);

    const configSource = process.env.CONFIG_SOURCE ||
    process.env.REACT_APP_CONFIG_SOURCE ||
    process.env.NODE_ENV ||
    'development';
    const statsigEnvironmentTier = getStatsigEnvironmentTier(configSource);

    const footerPlacementMinPageHeight = '80vh';

    useEffect(() => {
        const publicRoutes = ['/healthz'];

        if (!publicRoutes.includes(location.pathname)) {
            if (!user && !isLoadingUser && !isUserLoaded) {
                dispatch(getAuthUser());
            }
        }

        if (!isMemberTeamsLoaded && !isLoadingMemberTeams) {
            dispatch(getActiveTeams());
        }

        if (!isPreferencesLoaded && !isLoadingPreferences) {
            dispatch(getUserPreferences());
        }

        // Clean up URL hash after cookie consent modal closes
        const listenerName = 'hashchange';
        const handleHashChange = () => {
            // When the hash is empty (just "#" with nothing after), clean up the URL
            if (window.location.hash === '' || window.location.hash === '#') {
                window.history.replaceState(null, document.title, window.location.pathname + window.location.search);
            }
        };

        window.addEventListener(listenerName, handleHashChange);

        return () => {
            window.removeEventListener(listenerName, handleHashChange);
        };
    }, []);

    const showExpandedNav = !!user;

    const handleOnSocialMediaClick = (id) => {
        const socialMediaItem = FooterSocialMedia.find((item) => item?.id === id);
        window.open(socialMediaItem.href, '_blank', 'noopener');
    };

    return (
        <StatsigProvider
            sdkKey={config.statsigKey}
            user={{
                statsigEnvironment: {
                    tier: statsigEnvironmentTier,
                },
                userID: user?.id || null,
                email: user?.email || null,
            }}
            waitForInitialization
        >
            <StatsigContextProvider>
                <div
                    id="myutr-app-wrapper"
                    className={showExpandedNav ? 'expanded-nav' : ''}
                >
                    {isLoadingUser ? (
                        <Loading />
                    ) : (
                        <>
                            <Box sx={{ minHeight: footerPlacementMinPageHeight }}>
                                <Header />
                                <Menu />
                                <Routes />
                            </Box>
                            <Footer
                                exploreItems={FooterExploreItems}
                                providerItems={FooterProviderItems}
                                moreItems={FooterMoreItems}
                                supportItems={FooterSupportItems}
                                onSocialMediaClick={handleOnSocialMediaClick}
                                sx={{ mt: '64px' }}
                            />
                            <GenericPopup />
                            <ErrorPopup />
                        </>
                    )}
                </div>
            </StatsigContextProvider>
        </StatsigProvider>

    );
};

App.propTypes = {};

export default App;
